/**
 * DashboardsPanel — the shared two-pane "Dashboards" view used on both My Page
 * (personal) and Close Portal team pages (team).
 *
 * Layout: a left-hand list of QuickSight (FGBSBI) dashboards ranked by usage,
 * and a right-hand pane that embeds the selected dashboard.
 *
 * PHASE 1 (this component): UI shell only. The usage-ranking data source that
 * would populate the list ("top 10 dashboards used by the user / by the team's
 * LDAP group over the last 30 days") does not exist yet, so `dashboards`
 * defaults to an empty list and both panes render their empty states. When the
 * backend lands, pass in `dashboards` (and wire selection -> embed) without
 * changing this component's shape.
 *
 * Props:
 *   scope        {"user" | "team"}  who the ranking is for (drives copy).
 *   teamName     {string}           team label, used in copy when scope==="team".
 *   dashboards   {Array<{id, name, region?}>}  ranked dashboards (default []).
 *   loading      {boolean}          show skeletons instead of the empty state.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import "./homepage.scss";
import { getTopDashboards } from "src/utilities/CloseAPIs";

/**
 * @param {object} props
 * @param {"user"|"team"} props.scope
 * @param {string} [props.teamName]  team label (team scope only), for copy.
 * @param {string} [props.ldapGroup] the team's LDAP group id (team scope only);
 *                                   used as the DynamoDB scope id.
 */
function DashboardsPanel({ scope = "user", teamName, ldapGroup }) {
  const userId = useSelector((globalState) => globalState.auth.userId);

  const [selectedId, setSelectedId] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve the DynamoDB scope: USER#<alias> for My Page, TEAM#<ldap_group> for
  // a Close Portal team page.
  const scopeType = scope === "team" ? "TEAM" : "USER";
  const scopeId = scope === "team" ? ldapGroup : userId;

  useEffect(() => {
    let cancelled = false;
    if (!scopeId) {
      setDashboards([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getTopDashboards(scopeType, scopeId)
      .then((record) => {
        if (cancelled) return;
        // Generic TopItems payload: the ranked list lives in `items`.
        setDashboards(Array.isArray(record?.items) ? record.items : []);
      })
      .catch(() => {
        if (!cancelled) setDashboards([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeType, scopeId]);

  const introText = useMemo(() => {
    if (scope === "team") {
      return `Top 10 QuickSight dashboards used across ${
        teamName ? `the ${teamName} team` : "your team"
      }, ranked by usage over the last 30 days. Select a dashboard from the left to open it.`;
    }
    return "Your top 10 used QuickSight dashboards, ranked by your personal usage over the last 30 days. Select a dashboard from the left to open it.";
  }, [scope, teamName]);

  const selected = useMemo(
    () => dashboards.find((d) => d.id === selectedId) || null,
    [dashboards, selectedId]
  );

  return (
    // Narrow left list, wide right pane (matches the Figma proportions).
    <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
      {/* Left: ranked dashboard list */}
      <Container header={<Header variant="h2">Dashboards</Header>} disableContentPaddings={false}>
        {loading ? (
          <Box padding={{ vertical: "l" }} textAlign="center" color="text-body-secondary">
            Loading dashboards…
          </Box>
        ) : dashboards.length === 0 ? (
          <Box padding={{ vertical: "xxl" }} textAlign="center" color="inherit">
            <b>No dashboards yet</b>
            <Box variant="p" color="text-body-secondary" padding={{ top: "xs" }}>
              {scope === "team"
                ? "This team's most-used dashboards will appear here."
                : "Your most-used dashboards will appear here."}
            </Box>
          </Box>
        ) : (
          <SpaceBetween size="s">
            {dashboards.map((d) => (
              <div
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                style={{
                  cursor: "pointer",
                  color: d.id === selectedId ? "#0972d3" : "#0f141a",
                  fontWeight: d.id === selectedId ? 700 : 400,
                }}
              >
                {d.name}
              </div>
            ))}
          </SpaceBetween>
        )}
      </Container>

      {/* Right: compact intro banner over one large embed container. */}
      <SpaceBetween size="s">
        <Container>
          <Box
            display="inline-block"
            color="text-body-secondary"
            fontSize="body-s"
          >
            {introText}
          </Box>
        </Container>

        <Container>
          <div style={{ minHeight: "22rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {selected ? (
              // Placeholder — embedding is wired in a later phase once the
              // ranked-dashboard data source (region + dashboard id) exists.
              <Box textAlign="center" color="text-body-secondary">
                <b>{selected.name}</b>
                <Box variant="p" padding={{ top: "xs" }}>
                  Dashboard embedding will appear here.
                </Box>
              </Box>
            ) : (
              <Box textAlign="center" color="text-body-secondary">
                Select a dashboard from the left to open it.
              </Box>
            )}
          </div>
        </Container>
      </SpaceBetween>
    </Grid>
  );
}

export default DashboardsPanel;
